import {
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState,
    type KeyboardEvent,
} from "react";
import { CellContext } from "@tanstack/react-table";
import { TransactionListItem } from "@common/schemas/transaction.schema";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { Input } from "@design-system/Input/Input";
import { useLatestRef } from "@design-system/hooks/useLatestRef";
import { useViewOnlyRefusal } from "@hooks/permissions/useViewOnlyRefusal";
import { PaginatedTransactionsActionsContext } from "../PaginatedTransactionsTable.context";
import { useRegisterNoteCellOpener } from "./useRegisterNoteCellOpener";
import styles from "./InlineNoteCell.module.scss";

type InlineNoteCellProps = Pick<
    CellContext<ViewTableFeatures, TransactionListItem, unknown>,
    "row" | "table"
>;

/**
 * Always-visible memo input. Enter, blur, or the row unmounting mid-edit (a
 * virtualized row scrolled away) saves; Escape reverts to the saved memo. The
 * draft lives only while editing so server updates show otherwise; a failed
 * save puts the typed text back.
 */
export const InlineNoteCell = ({ row, table }: InlineNoteCellProps) => {
    const transaction = row.original;
    const savedMemo = transaction.memo ?? "";
    const { saveTransaction } = useContext(PaginatedTransactionsActionsContext);
    const { isViewOnly } = useViewOnlyRefusal();
    const inputRef = useRef<HTMLInputElement>(null);
    const shouldDiscardOnBlurRef = useRef(false);
    const [draft, setDraft] = useState<string | null>(null);
    // Mirrors `draft` synchronously so blur and unmount never save it twice.
    const draftRef = useRef<string | null>(null);
    // The text a failed save put back, until the user edits it. Unmounting
    // doesn't resend it unedited, so a permanent failure isn't retried on
    // every scroll.
    const failedDraftRef = useRef<string | null>(null);
    // Only the latest save may put its text back; an older one failing after
    // a newer save must not resurrect stale text.
    const latestSaveIdRef = useRef(0);

    const focusInput = useCallback(() => inputRef.current?.focus(), []);
    useRegisterNoteCellOpener({
        table,
        rowId: transaction.id,
        open: focusInput,
    });

    const updateDraft = (nextDraft: string | null) => {
        draftRef.current = nextDraft;
        setDraft(nextDraft);
    };

    const savePendingDraft = () => {
        const pendingDraft = draftRef.current;
        draftRef.current = null;
        if (pendingDraft === null) {
            return;
        }
        const nextMemo = pendingDraft.trim();
        if (nextMemo === savedMemo) {
            return;
        }
        // The failure toast comes from the API layer; putting the typed text
        // back keeps it from being lost so the next blur or Enter retries.
        latestSaveIdRef.current += 1;
        const saveId = latestSaveIdRef.current;
        saveTransaction(transaction, { memo: nextMemo }).catch(() => {
            if (
                saveId === latestSaveIdRef.current &&
                draftRef.current === null
            ) {
                failedDraftRef.current = pendingDraft;
                updateDraft(pendingDraft);
            }
        });
    };
    const savePendingDraftRef = useLatestRef(savePendingDraft);
    useEffect(
        () => () => {
            if (draftRef.current !== failedDraftRef.current) {
                savePendingDraftRef.current();
            }
        },
        [savePendingDraftRef],
    );

    const handleBlur = () => {
        const shouldDiscard = shouldDiscardOnBlurRef.current;
        shouldDiscardOnBlurRef.current = false;
        if (shouldDiscard) {
            draftRef.current = null;
        }
        savePendingDraft();
        setDraft(null);
    };

    // Enter and Escape must not reach row or task hotkeys (Escape closes the
    // task, Enter acts on the focused row). Other keys pass through so global
    // shortcuts enabled on form fields keep working.
    const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key !== "Enter" && event.key !== "Escape") {
            return;
        }
        event.stopPropagation();
        if (event.nativeEvent.isComposing) {
            return;
        }
        if (event.key === "Enter") {
            event.preventDefault();
            event.currentTarget.blur();
        } else if (event.key === "Escape") {
            event.preventDefault();
            shouldDiscardOnBlurRef.current = true;
            event.currentTarget.blur();
        }
    };

    return (
        <ViewTableCell captureClicks className={styles.container}>
            {/* Stays editable in read-only tables: the Reviewed tab locks every
                field except the memo. */}
            <Input
                ref={inputRef}
                size="md"
                value={draft ?? savedMemo}
                placeholder="Add memo"
                aria-label="Memo"
                readOnly={isViewOnly}
                onChange={(event) => {
                    failedDraftRef.current = null;
                    updateDraft(event.target.value);
                }}
                onBlur={handleBlur}
                onKeyDown={handleKeyDown}
            />
        </ViewTableCell>
    );
};
